/**
 * WishlistManager
 * localStorage-based wishlist for Dr. Hölter Shopify Theme.
 * No backend, no account required.
 */

class WishlistManager {
  constructor() {
    this.STORAGE_KEY = 'drh_wishlist';
    this.items = this.load();
  }

  load() {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  }

  save() {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.items));
    } catch {
      // localStorage full or unavailable
    }
  }

  get count() {
    return this.items.length;
  }

  has(productId) {
    const id = String(productId);
    return this.items.some(item => String(item.id) === id);
  }

  add(productId, handle) {
    const id = String(productId);
    if (this.has(id)) return;
    this.items.push({ id, handle, addedAt: Date.now() });
    this.save();
    document.dispatchEvent(new CustomEvent('wishlist:add', { detail: { id, handle } }));
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { count: this.count } }));
  }

  remove(productId) {
    const id = String(productId);
    const index = this.items.findIndex(item => String(item.id) === id);
    if (index === -1) return;
    const removed = this.items.splice(index, 1)[0];
    this.save();
    document.dispatchEvent(new CustomEvent('wishlist:remove', { detail: { id, handle: removed.handle } }));
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { count: this.count } }));
  }

  toggle(productId, handle) {
    if (this.has(productId)) {
      this.remove(productId);
    } else {
      this.add(productId, handle);
    }
  }

  getAll() {
    return [...this.items];
  }
}

// Singleton
window.wishlist = new WishlistManager();
document.dispatchEvent(new CustomEvent('wishlist:ready'));

/**
 * UI Binding — connects buttons and badges to the WishlistManager
 */
function initWishlistUI() {
  const wl = window.wishlist;

  // Update all toggle buttons to reflect current state
  function syncButtons() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(btn => {
      const productId = btn.dataset.productId;
      const isWished = wl.has(productId);
      btn.classList.toggle('is-wishlisted', isWished);
      btn.setAttribute('aria-pressed', String(isWished));
      const addLabel = btn.dataset.labelAdd || 'Add to wishlist';
      const removeLabel = btn.dataset.labelRemove || 'Remove from wishlist';
      btn.setAttribute('aria-label', isWished ? removeLabel : addLabel);
    });
  }

  // Update header badge
  function syncBadge() {
    document.querySelectorAll('[data-wishlist-count]').forEach(badge => {
      const count = wl.count;
      badge.textContent = count;
      badge.classList.toggle('hidden', count === 0);
      // Update parent link aria-label
      const link = badge.closest('[data-wishlist-header]');
      if (link) {
        link.style.display = count > 0 ? 'flex' : 'none';
        const template = link.dataset.wishlistLabelTemplate || 'Wishlist (__COUNT__)';
        link.setAttribute('aria-label', template.replace('__COUNT__', count));
      }
    });
  }

  // Click handler (delegated, capture phase to intercept before <a> navigation)
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const productId = btn.getAttribute('data-product-id');
    const productHandle = btn.getAttribute('data-product-handle');
    if (productId) wl.toggle(productId, productHandle);
  }, true);

  // Listen for state changes
  document.addEventListener('wishlist:change', () => {
    syncButtons();
    syncBadge();
  });

  // Initial sync
  syncButtons();
  syncBadge();
}

// Auto-init
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWishlistUI);
} else {
  initWishlistUI();
}

// Re-init on Shopify section load (Theme Editor)
document.addEventListener('shopify:section:load', initWishlistUI);
